/**
 * Edit Vendor Header Modal
 *
 * Modal for editing vendor header information including:
 * - Vendor Name (alias)
 * - Alternative Name
 * - Nickname
 * - Categories (multi-select)
 *
 * Uses React Hook Form + gray section styling matching create vendor wizard
 */

import { useMemo, useEffect } from 'react';
import { Modal, Input, Row, Col, Typography, theme, Radio } from 'antd';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { controlHeightTokens } from '@/styles/tokens';
import { useFormStyles } from '@/styles/formStyles';
import { useErrorHandler } from '@/lib/errors/useErrorHandler';
import { editVendorHeaderSchema } from '@/schemas/forms';
import type { VendorDetailResponse } from '@/types/api';
import type { CategoryListItem } from '@/types/api/category';
import { useVendorTranslation } from '@/hooks/useVendorTranslation';
import CategorySelectWithCreate from '@/components/shared/CategorySelectWithCreate';

const { Text } = Typography;

interface EditVendorHeaderFormData {
  name: string;
  alternativeName: string;
  nickname: string;
  type: 'company' | 'individual';
  categoryIds: number[];
  notes: string;
}

export interface EditVendorHeaderModalProps {
  open: boolean;
  onCancel: () => void;
  onSave: (data: EditVendorHeaderFormData) => Promise<void>;
  vendor: VendorDetailResponse | null;
  categories: CategoryListItem[];
  loadingCategories?: boolean;
  loading?: boolean;
  onCategoryCreated?: () => void | Promise<void>;
}

export const EditVendorHeaderModal: React.FC<EditVendorHeaderModalProps> = ({
  open,
  onCancel,
  onSave,
  vendor,
  categories,
  loadingCategories = false,
  loading = false,
  onCategoryCreated,
}) => {
  const { token } = theme.useToken();
  const formStyles = useFormStyles();
  const { t } = useVendorTranslation();
  const { handleError, handleSuccess } = useErrorHandler();

  // Compute form values from vendor data using useMemo (prevents unnecessary recalculations)
  // NOTE: Don't filter categoryIds based on categories list here - it would overwrite
  // newly created/selected categories when the list refreshes
  const formValues = useMemo(() => {
    if (!vendor) {
      return undefined;
    }

    return {
      name: vendor.name || '',
      alternativeName: vendor.alternativeName || '',
      nickname: vendor.nickname || '',
      type: (vendor.individual ? 'individual' : 'company') as 'company' | 'individual',
      categoryIds: vendor.categories?.map((c) => c.categoryId) || [],
      notes: vendor.notes || '',
    };
  }, [vendor]); // Only depend on vendor, not categories

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, touchedFields, isSubmitting },
  } = useForm<EditVendorHeaderFormData>({
    resolver: zodResolver(editVendorHeaderSchema),
    defaultValues: {
      name: '',
      alternativeName: '',
      nickname: '',
      type: 'company',
      categoryIds: [],
      notes: '',
    },
    // Use `values` prop for reactive updates from external data (recommended by RHF docs)
    values: formValues,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  // Reset form when modal opens to ensure fresh data from API
  useEffect(() => {
    if (open && formValues) {
      reset(formValues);
    }
  }, [open, formValues, reset]);

  const handleFormSubmit = async (data: EditVendorHeaderFormData) => {
    try {
      await onSave(data);
      handleSuccess(t('vendor.messages.vendorInfoUpdatedSuccess'));
      onCancel();
    } catch (error) {
      handleError(error);
    }
  };

  // Use standardized form styles (no custom overrides)
  const sectionTitleStyle = formStyles.sectionTitleStyle;
  const inputStyle = formStyles.inputStyle;

  return (
    <Modal
        title={t('vendor.modals.editVendorInformation')}
        open={open}
        onCancel={onCancel}
        onOk={handleSubmit(handleFormSubmit)}
        okText={t('vendor.actions.save')}
        cancelText={t('vendor.actions.cancel')}
        confirmLoading={isSubmitting || loading}
        width={800}
        okButtonProps={{
          style: { height: `${controlHeightTokens.controlHeightXL}px` },
        }}
        cancelButtonProps={{
          style: { height: `${controlHeightTokens.controlHeightXL}px` },
        }}
      >
        <form>
          {/* Vendor Information Section */}
          <div style={formStyles.fieldContainerStyle}>
            <Text strong style={sectionTitleStyle}>
              {t('vendor.sections.vendorInformation')}
            </Text>

            {/* Row 1: Type (身分) Radio + Vendor Name */}
            <Row gutter={[16, 16]} align="middle">
              <Col flex="auto">
                <Text style={formStyles.labelStyle}>
                  <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                  {t('vendor.fields.vendorName')}
                </Text>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: t('vendor.validation.vendorNameRequired') }}
                  render={({ field }) => (
                    <>
                      <Input
                        {...field}
                        size="large"
                        placeholder={t('vendor.placeholders.vendorName')}
                        style={{
                          ...inputStyle,
                          borderColor: errors.name && touchedFields.name ? token.colorError : undefined,
                        }}
                      />
                      {errors.name && touchedFields.name && (
                        <Text style={{ fontSize: token.fontSizeSM, color: token.colorError }}>
                          {t(errors.name.message!)}
                        </Text>
                      )}
                    </>
                  )}
                />
              </Col>
              <Col flex="none">
                <Text style={{ ...formStyles.labelStyle, visibility: 'hidden' }}>
                  {t('vendor.fields.type')}
                </Text>
                <Controller
                  name="type"
                  control={control}
                  render={({ field }) => (
                    <Radio.Group {...field} style={{ display: 'flex', height: '44px', alignItems: 'center' }}>
                      <Radio value="company">{t('vendor.fields.company')}</Radio>
                      <Radio value="individual">{t('vendor.fields.individual')}</Radio>
                    </Radio.Group>
                  )}
                />
              </Col>
            </Row>

            {/* Row 2: Alternative Name + Nickname (not required) */}
            <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
              <Col span={12}>
                <Text style={formStyles.labelStyle}>{t('vendor.fields.alternativeName')}</Text>
                <Controller
                  name="alternativeName"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      size="large"
                      placeholder={t('vendor.placeholders.alternativeName')}
                      style={formStyles.inputStyle}
                    />
                  )}
                />
              </Col>
              <Col span={12}>
                <Text style={formStyles.labelStyle}>{t('vendor.fields.nickname')}</Text>
                <Controller
                  name="nickname"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      size="large"
                      placeholder={t('vendor.placeholders.nickname')}
                      style={formStyles.inputStyle}
                    />
                  )}
                />
              </Col>
            </Row>

            {/* Row 3: Category */}
            <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
              <Col span={24}>
                <Text style={formStyles.labelStyle}>
                  <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                  {t('vendor.fields.category')}
                </Text>
                <Controller
                  name="categoryIds"
                  control={control}
                  rules={{
                    required: t('vendor.validation.categoryRequired'),
                    validate: (value) =>
                      (value && value.length > 0) || t('vendor.validation.categoriesRequired'),
                  }}
                  render={({ field, fieldState }) => (
                    <CategorySelectWithCreate
                      value={field.value}
                      onChange={field.onChange}
                      categories={categories}
                      loading={loadingCategories}
                      onCategoryCreated={onCategoryCreated}
                      error={
                        fieldState.error?.message
                          ? t(fieldState.error.message)
                          : undefined
                      }
                      placeholder={t('vendor.placeholders.categories')}
                    />
                  )}
                />
              </Col>
            </Row>

            {/* Row 4: Notes */}
            <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
              <Col span={24}>
                <Text style={formStyles.labelStyle}>{t('vendor.fields.notes')}</Text>
                <Controller
                  name="notes"
                  control={control}
                  render={({ field }) => (
                    <Input.TextArea
                      {...field}
                      rows={3}
                      placeholder={t('vendor.placeholders.notes')}
                      style={{
                        ...formStyles.inputStyle,
                        height: 'auto',
                      }}
                    />
                  )}
                />
              </Col>
            </Row>
          </div>
        </form>
      </Modal>
  );
};

export default EditVendorHeaderModal;
